import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Empty,
  Table,
  Tag,
  Timeline,
  Typography,
  Space,
  Spin,
} from 'antd';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  timelinePresentation,
  timelineSummary,
  timeText,
} from './SimpleWorkspaceSupport';
import ActionProfileSummary from './ActionProfileSummary';
import styles from './SimpleWorkspace.less';

const { Paragraph, Text } = Typography;

export default function SimpleWorkspaceTabs({ model, actions, columns }) {
  const {
    activeTab,
    memberId,
    actionLoading,
    actionRecords,
    actionPage,
    actionProfilesLoading,
    actionProfiles,
    actionProfileCurrent,
    courseLoading,
    courseRecords,
    courseCurrent,
    coursePage,
    communicationLoading,
    communicationRows,
    communicationCurrent,
    communicationEvidenceCount,
    communicationPage,
    capabilityAccounts,
    timelineLoading,
    timelineEvidence,
    timelinePage,
  } = model;

  const openTimelineWithKeyboard = (event, record) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      actions.openTimelineEvent(record);
    }
  };

  const paneContent = {
    action: (
      <Card bordered={false} className={styles.tabsCard}>
        <Table
          className={styles.dataTable}
          rowKey="evaluationId"
          loading={actionLoading}
          dataSource={actionRecords}
          columns={columns.actionColumns}
          pagination={{
            current: actionPage.current,
            pageSize: actionPage.pageSize,
            total: actionPage.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条动作评估`,
            onChange: (current, pageSize) =>
              actions.loadActionPage(memberId, current, pageSize),
          }}
          locale={{ emptyText: '暂无动作评估数据' }}
        />
      </Card>
    ),
    'action-profile': (
      <Card bordered={false} className={styles.tabsCard}>
        <ActionProfileSummary profiles={actionProfiles} />
        <Table
          className={styles.dataTable}
          rowKey="actionId"
          loading={actionProfilesLoading}
          dataSource={actionProfiles}
          columns={columns.actionProfileColumns}
          pagination={{
            current: actionProfileCurrent,
            pageSize: DEFAULT_TABLE_PAGE_SIZE,
            showSizeChanger: false,
            showTotal: (total) => `共训练过 ${total} 个动作`,
            onChange: actions.setActionProfileCurrent,
          }}
          scroll={{ x: 1190 }}
          locale={{ emptyText: '暂无动作训练记录' }}
        />
      </Card>
    ),
    course: (
      <Card bordered={false} className={styles.tabsCard}>
        <Table
          className={styles.dataTable}
          rowKey="roomId"
          loading={courseLoading}
          dataSource={courseRecords}
          columns={columns.courseColumns}
          pagination={{
            current: courseCurrent,
            pageSize: DEFAULT_TABLE_PAGE_SIZE,
            total:
              courseRecords.length +
              (coursePage.hasMore ? DEFAULT_TABLE_PAGE_SIZE : 0),
            showSizeChanger: false,
            showTotal: () =>
              coursePage.hasMore
                ? `已加载 ${courseRecords.length} 节课程，还有更多记录`
                : `共 ${courseRecords.length} 节课程`,
            onChange: actions.changeCoursePage,
          }}
          scroll={{ x: 981 }}
          locale={{ emptyText: '暂无上课记录' }}
        />
      </Card>
    ),
    communication: (
      <Card bordered={false} className={styles.tabsCard}>
        <Table
          rowKey="evidenceId"
          loading={communicationLoading}
          dataSource={communicationRows}
          columns={columns.communicationColumns}
          pagination={{
            current: communicationCurrent,
            pageSize: DEFAULT_TABLE_PAGE_SIZE,
            total:
              communicationEvidenceCount +
              (communicationPage.hasMore ? DEFAULT_TABLE_PAGE_SIZE : 0),
            showSizeChanger: false,
            showTotal: () =>
              communicationPage.hasMore
                ? `已加载 ${communicationEvidenceCount} 条沟通观察，还有更多记录`
                : `共 ${communicationEvidenceCount} 条沟通观察`,
            onChange: actions.changeCommunicationPage,
          }}
          scroll={{ x: 1050 }}
          locale={{ emptyText: '暂无企微或 1V1 真人课训练沟通观察' }}
        />
      </Card>
    ),
    capability: (
      <Card bordered={false} className={styles.tabsCard}>
        <Table
          className={styles.dataTable}
          rowKey={(record) => record.snapshotId || record.dimensionCode}
          dataSource={capabilityAccounts}
          columns={columns.capabilityColumns}
          pagination={false}
          scroll={{ x: 1320 }}
          locale={{ emptyText: '暂无运动能力数据' }}
        />
      </Card>
    ),
    archive: (
      <Card
        bordered={false}
        title="会员活动时间轴"
        extra={<Text type="secondary">按实际发生时间倒序</Text>}
        className={`${styles.tabsCard} ${styles.timelineCard}`}
      >
        <Spin spinning={timelineLoading && !timelineEvidence.length}>
          {timelineEvidence.length ? (
            <Timeline className={styles.activityTimeline}>
              {timelineEvidence.map((record) => {
                const presentation = timelinePresentation(record);
                return (
                  <Timeline.Item
                    key={record.evidenceId}
                    color={presentation.color}
                  >
                    <Card
                      size="small"
                      hoverable
                      role="button"
                      tabIndex={0}
                      className={styles.timelineEventCard}
                      onClick={() => actions.openTimelineEvent(record)}
                      onKeyDown={(event) =>
                        openTimelineWithKeyboard(event, record)
                      }
                    >
                      <div className={styles.timelineEventHeader}>
                        <Space size={8} wrap>
                          <Tag color={presentation.color}>
                            {presentation.category}
                          </Tag>
                          <Text strong>{presentation.title}</Text>
                        </Space>
                        <Space size={12}>
                          <Text type="secondary">
                            {timeText(record.occurredAt)}
                          </Text>
                          <Text
                            type="secondary"
                            className={styles.timelineDetailAction}
                          >
                            查看详情
                          </Text>
                        </Space>
                      </div>
                      <Paragraph
                        ellipsis={{ rows: 2 }}
                        className={styles.timelineEventSummary}
                      >
                        {timelineSummary(record)}
                      </Paragraph>
                    </Card>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                timelineLoading
                  ? '正在读取会员活动时间轴'
                  : '暂无已归档的会员活动记录'
              }
            />
          )}
          {timelinePage.hasMore && (
            <div className={styles.timelineLoadMore}>
              <Button
                loading={timelineLoading}
                onClick={actions.loadMoreTimeline}
              >
                加载更早记录
              </Button>
            </div>
          )}
        </Spin>
      </Card>
    ),
  };

  return (
    paneContent[activeTab] || (
      <Card className={styles.infoCard} bordered={false}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="该训练档案页面已调整或暂不可用"
        >
          <Button
            type="primary"
            onClick={() => actions.changeActiveTab('overview')}
          >
            返回训练概览
          </Button>
        </Empty>
      </Card>
    )
  );
}

SimpleWorkspaceTabs.propTypes = {
  model: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  columns: PropTypes.object.isRequired,
};
